import React, { useState, useEffect } from 'react';
import {
  Star,
  ShoppingBag,
  Heart,
  Eye,
  CheckCircle2,
  Download,
  Layers,
  Sparkles,
  Zap,
  ChevronLeft,
  ChevronRight,
  ImageIcon
} from 'lucide-react';
import { Product, Currency } from '../types';
import { getSafeProductImages, hasProductRealImage, KNOWN_DELETED_IDS } from '../lib/productImages';
import { isProductPublished } from '../lib/statsService';
import { getDeletedProductIds } from '../lib/productsService';

interface ProductCardProps {
  product: Product;
  currency: Currency;
  isInWishlist: boolean;
  onToggleWishlist: (p: Product) => void;
  onAddToCart: (p: Product) => void;
  onQuickView: (p: Product) => void;
  onQuickBuy?: (p: Product) => void;
  darkMode: boolean;
  priority?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = React.memo(({
  product,
  currency,
  isInWishlist,
  onToggleWishlist,
  onAddToCart,
  onQuickView,
  onQuickBuy,
  darkMode,
  priority = false,
}) => {
  // All Hooks MUST be called unconditionally at the top of the component to adhere to React Rules of Hooks
  const [imageError, setImageError] = useState(false);
  const [activeImgIdx, setActiveImgIdx] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  // Gallery Images with safe document template fallback
  const images = React.useMemo(() => (product ? getSafeProductImages(product) : []), [product]);

  // Reset image error state and active index if product ID changes
  useEffect(() => {
    setImageError(false);
    setActiveImgIdx(0);
  }, [product?.id]);

  // Keep activeImgIdx in valid bounds if images list changes
  useEffect(() => {
    if (activeImgIdx >= images.length) {
      setActiveImgIdx(0);
    }
  }, [images.length, activeImgIdx]);

  // Auto-play slideshow on hover when multiple images exist
  useEffect(() => {
    if (!isHovered || images.length <= 1) return;
    const timer = setInterval(() => {
      setActiveImgIdx(prev => (prev + 1) % images.length);
    }, 2400);
    return () => clearInterval(timer);
  }, [isHovered, images.length]);

  // Never render a deleted product on the storefront
  // Executed strictly AFTER all hooks to prevent React hook mismatch error #300 across browser renders
  if (
    !product ||
    !product.id ||
    (product as any).isDeleted === true ||
    product.status === 'deleted' ||
    KNOWN_DELETED_IDS.includes(product.id) ||
    getDeletedProductIds().has(product.id) ||
    !isProductPublished(product)
  ) {
    return null;
  }

  const currencySymbols: Record<Currency, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
  };

  const exchangeRates: Record<Currency, number> = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.78,
  };

  const symbol = currencySymbols[currency] || '$';
  const rate = exchangeRates[currency] || 1;

  const priceNum = Number(product.price) || 0;
  const origPriceNum = Number(product.originalPrice) || priceNum;
  const priceFormatted = (priceNum * rate).toFixed(2);
  const origPriceFormatted = (origPriceNum * rate).toFixed(2);
  const discountVal = product.discountPercent ?? (origPriceNum > priceNum ? Math.round(((origPriceNum - priceNum) / origPriceNum) * 100) : 0);

  const handlePrevImage = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setActiveImgIdx(prev => (prev - 1 + images.length) % images.length);
  };

  const handleNextImage = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setActiveImgIdx(prev => (prev + 1) % images.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX;
    if (deltaX > 35) {
      handlePrevImage();
    } else if (deltaX < -35) {
      handleNextImage();
    }
    setTouchStartX(null);
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group relative rounded-3xl transition-all duration-500 md:hover:-translate-y-2 border flex flex-col justify-between overflow-hidden ${
        darkMode ? 'glass-card border-white/10 hover:border-cyan-500/50 hover:shadow-2xl hover:shadow-cyan-500/10' : 'glass-card-light border-slate-200 hover:border-blue-500/50 hover:shadow-2xl'
      }`}
    >
      
      {/* Top Media Preview Container & 3D Interactive Image Gallery Slider */}
      <div
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className="relative aspect-[16/10] overflow-hidden bg-slate-950/40 select-none group/slider"
      >
        <img
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          src={images[activeImgIdx] || images[0] || ''}
          alt={`${product.title} - Image ${activeImgIdx + 1} - RH Editography Template`}
          title={`${product.title} - RH Editography ${product.category} Template`}
          onError={(e) => {
            const target = e.currentTarget;
            if (activeImgIdx !== 0 && images[0] && target.src !== images[0]) {
              target.src = images[0];
            } else {
              setImageError(true);
              const fallback = getSafeProductImages(product)[0];
              if (fallback && target.src !== fallback) {
                target.src = fallback;
              }
            }
          }}
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover md:group-hover:scale-110 transition-all duration-700 ease-out"
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-black/30 pointer-events-none"></div>

        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap items-center gap-1.5 z-10">
          {product.isPopular && (
            <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 text-[10px] font-black uppercase tracking-wider shadow-md">
              POPULAR
            </span>
          )}
          {discountVal > 0 && (
            <span className="px-2.5 py-1 rounded-full bg-slate-900/95  text-white text-[10px] font-extrabold border border-white/20">
              -{discountVal}%
            </span>
          )}
          {images.length > 1 && (
            <span className="px-2 py-0.5 rounded-full bg-slate-950 text-cyan-300 font-mono text-[10px] font-black border border-cyan-500/40  shadow-md flex items-center gap-1">
              <ImageIcon className="w-3 h-3" />
              {activeImgIdx + 1}/{images.length}
            </span>
          )}
        </div>

        {/* Wishlist Button (Top Right) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleWishlist(product);
          }}
          className={`absolute top-3 right-3 p-2 rounded-xl  border transition-all z-20 ${
            isInWishlist
              ? 'bg-rose-500 text-white border-rose-400 scale-110'
              : 'bg-slate-900/60 text-slate-300 border-white/20 hover:bg-rose-500 hover:text-white'
          }`}
          title={isInWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
        >
          <Heart className={`w-4 h-4 ${isInWishlist ? 'fill-white' : ''}`} />
        </button>

        {/* Image Slider Left / Right Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-xl bg-slate-950/95 hover:bg-cyan-500 text-white hover:text-slate-950 border border-white/20 z-20 opacity-0 group-hover/slider:opacity-100 transition-all duration-300 shadow-xl"
              title="Previous image slide"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-xl bg-slate-950/95 hover:bg-cyan-500 text-white hover:text-slate-950 border border-white/20 z-20 opacity-0 group-hover/slider:opacity-100 transition-all duration-300 shadow-xl"
              title="Next image slide"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Pagination Dots */}
            <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20 bg-slate-950/70 px-2 py-1 rounded-full  border border-white/10">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImgIdx(idx);
                  }}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    activeImgIdx === idx ? 'w-4 bg-cyan-400 shadow-sm shadow-cyan-400' : 'w-1.5 bg-white/40 hover:bg-white'
                  }`}
                  title={`View image ${idx + 1}`}
                />
              ))}
            </div>
          </>
        )}

        {/* Formats Pills on Preview Bottom Left */}
        <div className="absolute bottom-3 left-3 flex gap-1.5 z-10">
          {(product.formats || ['PSD', 'AI']).map((fmt) => (
            <span
              key={fmt}
              className="px-2 py-0.5 rounded bg-black/70  text-[10px] font-mono font-bold text-cyan-300 border border-cyan-500/30"
            >
              .{fmt}
            </span>
          ))}
        </div>

        {/* Hover Quick View Overlay Action */}
        <div className="absolute inset-0 bg-slate-950/95  opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3 pointer-events-none group-hover:pointer-events-auto">
          <button
            onClick={() => onQuickView(product)}
            className="px-4 py-2.5 rounded-xl bg-white text-slate-950 font-extrabold text-xs flex items-center gap-1.5 shadow-xl hover:scale-105 active:scale-95 transition-transform"
          >
            <Eye className="w-4 h-4 text-cyan-600" />
            <span>3D Quick View</span>
          </button>
        </div>
      </div>

      {/* Card Details Body */}
      <div className="p-5 flex-1 flex flex-col justify-between gap-4">
        <div>
          {/* Category & Rating */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[11px] font-extrabold text-cyan-400 uppercase tracking-wider">
              {product.category || 'Vector Asset'}
            </span>
            <div className="flex items-center gap-1 text-amber-400 text-xs font-bold">
              <Star className="w-3.5 h-3.5 fill-amber-400" />
              <span>{product.rating ?? 5.0}</span>
              <span className="text-slate-400 font-normal">({product.reviewCount ?? 0})</span>
            </div>
          </div>

          {/* Title */}
          <h3
            onClick={() => onQuickView(product)}
            className="font-extrabold text-base line-clamp-2 cursor-pointer hover:text-cyan-400 transition-colors leading-snug"
          >
            {product.title || 'RH Editography Design Template'}
          </h3>

          {/* Feature Micro Specs */}
          <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-400 font-medium">
            <span className="flex items-center gap-1">
              <Layers className="w-3.5 h-3.5 text-blue-400" />
              {product.specs?.layers || 'Smart Object Layers'}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              {product.specs?.dpi || 300} DPI
            </span>
          </div>
        </div>

        {/* Footer Price & Add to Cart */}
        <div className="pt-3 border-t border-white/10 flex items-center justify-between gap-2">
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl font-black text-gradient">
                {symbol}{priceFormatted}
              </span>
              <span className="text-xs line-through text-slate-500 font-semibold">
                {symbol}{origPriceFormatted}
              </span>
            </div>
            <p className="text-[10px] text-emerald-400 font-bold">Instant Zip Delivery</p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => onAddToCart(product)}
              className="px-2.5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-extrabold text-xs flex items-center gap-1.5 transition-colors border border-slate-700 hover:border-slate-600"
              title="Add to Cart"
            >
              <ShoppingBag className="w-4 h-4" />
            </button>
            {onQuickBuy && (
              <button
                onClick={() => onQuickBuy(product)}
                className="px-3.5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-white font-extrabold text-xs flex items-center gap-1.5 shadow-lg shadow-emerald-500/20 hover:shadow-teal-500/40 hover:scale-105 active:scale-95 transition-all duration-200"
              >
                <Zap className="w-3.5 h-3.5" />
                <span>Buy</span>
              </button>
            )}
          </div>
        </div>

      </div>

    </div>
  );
});
